/* eslint-disable jsdoc/check-param-names */
import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Chip, Autocomplete, Button } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import { BasePopup, PopupSubtitle } from '../shared/BasePopup.jsx';
import { debounce } from 'lodash';
import { SearchTextfield } from './SearchTextfield.jsx';
import { Context } from 'olympe';

/**
 * Popup "Add New Cost Center/WBS To Release Strategies"
 * @param {boolean} open
 * @param {function(): void} handleCloseDialog
 * @param {function(): void} handleGetLevel1ForEmpty
 * @param {object} costObject
 * @param {string} objectType
 * @param {Context} context
 * @returns {React.ReactElement}
 * @class
 */

export const AddCostObjectDialog = ({
    open,
    handleCloseDialog,
    handleGetLevel1ForEmpty,
    costObject,
    objectType,
    context,
}) => {
    const getLevelPurchasingGroups = context.get('Get Purchasing Group');
    const getPlants = context.get('Get Plants');
    const getPurchOrgs = context.get('Get Purchasing Organizations');
    const operation = context.get('Operation');

    const [plants, setPlants] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState(null);
    const [onSelectPlant, setOnSelectPlant] = useState(true);

    const [purchOrgs, setPurchOrgs] = useState([]);
    const [selectedPurchOrg, setSelectedPurchOrg] = useState(null);
    const [onSelectPurchOrg, setOnSelectPurchOrg] = useState(true);

    const [levelPurchasingGroups, setLevelPurchasingGroups] = useState([]);
    const [selectedLevelPurchasingGroup, setSelectedLevelPurchasingGroup] = useState(null);
    const [inputLevelPurchasingGroup, setInputLevelPurchasingGroup] = useState('');
    const [onSelectLevelPurchasingGroup, setOnSelectLevelPurchasingGroup] = useState(true);

    let getObjectId, getObjectLabel;
    if (objectType === 'Cost Center') {
        getObjectId = (obj) => (obj.CostElementId ? obj.CostElementId : obj.CostCenterId);
        getObjectLabel = (obj) =>
            `${obj.CostElementId ? obj.CostElementId : obj.CostCenterId} - ${
                obj.CostElementDescription ? obj.CostElementDescription : obj.CostCenterDescription
            }`;
    } else if (objectType === 'Purchase Group') {
        getObjectId = (obj) => obj.PurchGroup;
        getObjectLabel = (obj) => `${obj.PurchGroup} - ${obj.PurchGroupName}`;
    } else {
        getObjectId = (obj) => (obj.PrnNo ? obj.PrnNo : obj.CostElementId);
        getObjectLabel = (obj) =>
            `${obj.PrnNo ? obj.PrnNo : obj.CostElementId} - ${
                obj.CostElementDescription ? obj.CostElementDescription : obj.PrnName
            }`;
    }

    useEffect(() => {
        const [triggerInputPlants, costObjectInput] = getPlants.getInputs();

        // eslint-disable-next-line no-unused-vars
        const [SuccessPlants, ErrorPlants, arrayPlants] = getPlants.getOutputs();

        const getPlants$ = context
            .runner(getPlants)
            .set(costObjectInput, getObjectId(costObject))
            .trigger(triggerInputPlants);

        getPlants$.waitFor(SuccessPlants).then(() => {
            setPlants(getPlants$.get(arrayPlants));
        });

        if (operation === 'PO') {
            const [triggerInputPurchOrgs, purchGroupInput] = getPurchOrgs.getInputs();
            // eslint-disable-next-line no-unused-vars
            const [SuccessPurchOrg, ErrorPurchOrg, arrayPurchOrgs] = getPurchOrgs.getOutputs();

            const getPurchOrgs$ = context
                .runner(getPurchOrgs)
                .set(purchGroupInput, getObjectId(costObject))
                .trigger(triggerInputPurchOrgs);

            getPurchOrgs$.waitFor(SuccessPurchOrg).then(() => {
                setPurchOrgs(getPurchOrgs$.get(arrayPurchOrgs));
            });
        }
    }, []);

    const handleGetLevel1ForEmptyAndClose = (plant, purchasingGroup, purchasingOrg) => (event) => {
        handleCloseDialog();
        if (objectType === 'Purchase Group') {
            handleGetLevel1ForEmpty(plant, costObject, purchasingOrg);
        } else {
            handleGetLevel1ForEmpty(plant, purchasingGroup, purchasingOrg);
        }
    };

    const queryLevelPurchasingGroup = (text) => {
        const [triggerInputLevelPurchasingGroup, searchText] = getLevelPurchasingGroups.getInputs();

        // eslint-disable-next-line no-unused-vars
        const [SuccessLevelPurchasingGroup, ErrorLevelPurchasingGroup, arrayLevelPurchasingGroup] =
            getLevelPurchasingGroups.getOutputs();

        const getLevelPurchasingGroups$ = context
            .runner(getLevelPurchasingGroups)
            .set(searchText, text)
            .trigger(triggerInputLevelPurchasingGroup);

        getLevelPurchasingGroups$.waitFor(SuccessLevelPurchasingGroup).then(() => {
            setLevelPurchasingGroups(getLevelPurchasingGroups$.get(arrayLevelPurchasingGroup));
        });
    };
    const debouncedQuery = useMemo(() => debounce(queryLevelPurchasingGroup, 300), []);

    return (
        open && (
            <BasePopup
                title={`Add new ${objectType} to Release Strategies`}
                content={
                    <>
                        <Typography variant="body2" sx={{ color: '#5D5858', marginBottom: '1vh' }} align="justify">
                            In order to help us identifying the correct release strategies for your organisation, please
                            provide any of the purchasing groups set on your release strategies
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#5D5858', marginBottom: '36px' }} align="justify">
                            On the next screen, you will be allowed to link the {objectType.toLowerCase()} you want to
                            update to the relevant release strategies
                        </Typography>
                        <div style={{ marginBottom: '36px' }}>
                            <PopupSubtitle subtitle={objectType} />
                            <Chip color="primary" label={getObjectLabel(costObject)} />
                        </div>
                        {
                            /* For Cost Center and WBS => display Plant and Purchasing Group fields */
                            /* For Purchase Group => display Purchasing Org field */
                            objectType !== 'Purchase Group' && (
                                <>
                                    <div style={{ marginBottom: '36px' }}>
                                        <PopupSubtitle subtitle="Plants" />
                                        {selectedPlant && onSelectPlant === false && (
                                            <Chip
                                                label={`${selectedPlant.PlantCode} - ${selectedPlant.PlantName}`}
                                                icon={<CachedIcon />}
                                                color="primary"
                                                onClick={() => setOnSelectPlant(true)}
                                            />
                                        )}
                                        {onSelectPlant === true && (
                                            <Autocomplete
                                                sx={{ marginBottom: '1vh' }}
                                                value={selectedPlant}
                                                onChange={(event, newValue) => {
                                                    setSelectedPlant(newValue);
                                                    setOnSelectPlant(false);
                                                }}
                                                loadingText="...Loading"
                                                options={plants}
                                                disableClearable={true}
                                                getOptionLabel={(params) => {
                                                    if (params) {
                                                        return params.PlantCode
                                                            ? `${params.PlantCode} - ${params.PlantName}`
                                                            : params.PlantName;
                                                    }
                                                }}
                                                size={'small'}
                                                renderInput={(params) => (
                                                    <SearchTextfield params={params} exampleFormat="Z200 or Nestle" />
                                                )}
                                            />
                                        )}
                                    </div>
                                    <div style={{ marginBottom: '36px' }}>
                                        <PopupSubtitle subtitle="Purchasing Group (Optional)" />
                                        {selectedLevelPurchasingGroup && onSelectLevelPurchasingGroup === false && (
                                            <Chip
                                                label={`${selectedLevelPurchasingGroup.PurchGroup} - ${selectedLevelPurchasingGroup.PurchGroupName}`}
                                                color="primary"
                                                icon={<CachedIcon />}
                                                onClick={() => setOnSelectLevelPurchasingGroup(true)}
                                            />
                                        )}
                                        {onSelectLevelPurchasingGroup === true && (
                                            <Autocomplete
                                                sx={{ marginBottom: '1vh' }}
                                                value={selectedLevelPurchasingGroup}
                                                onChange={(event, newValue) => {
                                                    setSelectedLevelPurchasingGroup(newValue);
                                                    setOnSelectLevelPurchasingGroup(false);
                                                }}
                                                inputValue={inputLevelPurchasingGroup}
                                                onInputChange={(e, value, reason) => {
                                                    if (reason === 'reset') {
                                                        setInputLevelPurchasingGroup(''); // if a value has been selected, clear the textfield
                                                    } else {
                                                        setInputLevelPurchasingGroup(value);
                                                    }
                                                    if (reason === 'input' && value.length > 1) {
                                                        debouncedQuery(value);
                                                    }
                                                }}
                                                loadingText="...Loading"
                                                options={levelPurchasingGroups}
                                                disableClearable={true}
                                                getOptionLabel={(params) => {
                                                    if (params) {
                                                        return params.PurchGroup
                                                            ? `${params.PurchGroup} - ${params.PurchGroupName}`
                                                            : params.PurchGroupName;
                                                    }
                                                }}
                                                size={'small'}
                                                renderInput={(params) => (
                                                    <SearchTextfield params={params} exampleFormat="001 or Buyer" />
                                                )}
                                            />
                                        )}
                                    </div>
                                </>
                            )
                        }
                        {objectType === 'Purchase Group' && (
                            <>
                                <div style={{ marginBottom: '36px' }}>
                                    <PopupSubtitle subtitle="Purchasing Organization" />
                                    {selectedPurchOrg && onSelectPurchOrg === false && (
                                        <Chip
                                            label={`${selectedPurchOrg.Ekorg} - ${selectedPurchOrg.Ekotx}`}
                                            icon={<CachedIcon />}
                                            color="primary"
                                            onClick={() => setOnSelectPurchOrg(true)}
                                        />
                                    )}
                                    {onSelectPurchOrg === true && (
                                        <Autocomplete
                                            sx={{ marginBottom: '1vh' }}
                                            value={selectedPurchOrg}
                                            onChange={(event, newValue) => {
                                                setSelectedPurchOrg(newValue);
                                                setOnSelectPurchOrg(false);
                                            }}
                                            loadingText="...Loading"
                                            options={purchOrgs}
                                            disableClearable={true}
                                            getOptionLabel={(params) => {
                                                if (params) {
                                                    return params.Ekorg
                                                        ? `${params.Ekorg} - ${params.Ekotx}`
                                                        : params.Ekotx;
                                                }
                                            }}
                                            size={'small'}
                                            renderInput={(params) => (
                                                <SearchTextfield
                                                    params={params}
                                                    exampleFormat="Example Format to be provided"
                                                />
                                            )}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </>
                }
                button={
                    <>
                        <Button
                            color="primary"
                            variant="contained"
                            className="base-popup-button"
                            style={{ marginRight: '21px' }}
                            disabled={objectType !== 'Purchase Group' && selectedPlant === null}
                            onClick={handleGetLevel1ForEmptyAndClose(
                                selectedPlant,
                                selectedLevelPurchasingGroup,
                                selectedPurchOrg
                            )}
                        >
                            Search Release Strategies
                        </Button>
                        <Button
                            color="secondary"
                            variant="outlined"
                            className="base-popup-button"
                            onClick={handleCloseDialog}
                        >
                            clear
                        </Button>
                    </>
                }
                handleCloseDialog={handleCloseDialog}
                style={{ maxWidth: '676px' }}
            />
        )
    );
};
